import { datadogRum } from '@datadog/browser-rum';
import { redirect } from '@tanstack/react-router';
import { FEATURE_FLAGS_KEYS } from 'constants/featureFlags';
import { featureFlagGuard } from './feature-flag-guard';
import { UserPermission, userPermissionGuard } from './user-permission-guard';

export enum GUARD_PREFIXES {
  FEATURE_FLAG = 'FF_',
  USER_PERMISSION = 'UP_',
}

// Type-safe guard key definitions using template literal types
type FeatureFlagKeys = keyof typeof FEATURE_FLAGS_KEYS;
type UserPermissionKeys = keyof typeof UserPermission;

// Union type for all valid guard keys
export type GuardKey = `FF_${FeatureFlagKeys}` | `UP_${UserPermissionKeys}`;

/**
 * Type-safe guard key builders for better DX
 */
export const GuardKeyBuilder = {
  featureFlag: <K extends FeatureFlagKeys>(key: K): `FF_${K}` => `FF_${key}`,
  userPermission: <K extends UserPermissionKeys>(key: K): `UP_${K}` => `UP_${key}`,
} as const;

/**
 * Utility function to handle error logging and DataDog reporting
 *
 * @param error - The error to handle
 * @param message - Message for console
 * @param context - Additional context data for DataDog
 */
const handleGuardError = (error: unknown, message: string, context?: Record<string, unknown>): void => {
  const errorInstance = error instanceof Error ? error : new Error(String(error));

  console.error(message, errorInstance);
  datadogRum.addError(errorInstance, { info: `${message}: ${errorInstance.message}`, ...context });
};

// Guard handler registry - using Map for O(1) lookups
const guardHandlers = new Map([
  [
    GUARD_PREFIXES.FEATURE_FLAG,
    async (key: string): Promise<boolean> => {
      const featureFlag = FEATURE_FLAGS_KEYS[key as FeatureFlagKeys];

      if (!featureFlag) {
        console.warn(`Unknown feature flag: ${key}`);
        return false;
      }

      return featureFlagGuard(featureFlag);
    },
  ],
  [
    GUARD_PREFIXES.USER_PERMISSION,
    async (key: string): Promise<boolean> => {
      const permission = UserPermission[key as UserPermissionKeys];

      if (permission === undefined) {
        console.warn(`Unknown user permission: ${key}`);
        return false;
      }

      return userPermissionGuard(permission);
    },
  ],
]);

// The ESLint rule exists to prevent accidental sequential execution when parallel would be better, but in our cases are intentionally sequential for performance reasons.
/* eslint-disable no-await-in-loop */

/**
 * Validates a single guard based on its type
 *
 * @param guard - The guard string (e.g., "FF_SOME_FEATURE", "UP_ADMIN")
 * @returns Promise<boolean> - Whether the guard validation passed
 */
export const validateGuard = async (guard: GuardKey): Promise<boolean> => {
  try {
    for (const [prefix, handler] of guardHandlers) {
      if (guard.startsWith(prefix)) {
        return await handler(guard.slice(prefix.length));
      }
    }

    console.warn(`Unknown guard type: ${guard}`);
    return false;
  } catch (error) {
    handleGuardError(error, `Unexpected error during guard validation: ${guard}`);
    return false;
  }
};

/**
 * Validates multiple guards using AND logic (all must pass)
 *
 * @param guards - Array of guard strings
 * @returns Promise<boolean> - Whether all guards passed validation
 */
export const validateGuards = async (guards: GuardKey[]): Promise<boolean> => {
  if (!guards?.length) {
    return true; // No guards means access is allowed
  }

  try {
    for (const guard of guards) {
      const hasPassed = await validateGuard(guard);

      if (!hasPassed) {
        return false; // Short-circuit: if any guard fails, return false immediately
      }
    }
    return true;
  } catch (error) {
    handleGuardError(error, 'Unexpected error during guards validation (AND logic)', { guards });
    return false;
  }
};

/**
 * Validates multiple guards using OR logic (any must pass)
 *
 * @param guards - Array of guard strings
 * @returns Promise<boolean> - Whether any guard passed validation
 */
export const validateGuardsOr = async (guards: GuardKey[]): Promise<boolean> => {
  if (!guards?.length) {
    return true; // No guards means access is allowed
  }

  try {
    const guardResults = await Promise.allSettled(guards.map(validateGuard));

    return guardResults.some((result) => result.status === 'fulfilled' && result.value);
  } catch (error) {
    handleGuardError(error, 'Unexpected error during guards validation (OR logic)', { guards });
    return false;
  }
};

/* eslint-disable @typescript-eslint/no-throw-literal */
/**
 * Validates guards and throws redirect if any fail - convenience function for routes
 *
 * @param guards - Array of guard strings
 * @param redirectTo - Where to redirect if guards fail (default: '/spaces')
 * @throws redirect if guards fail
 */
export const validateGuardsOrRedirect = async (guards: GuardKey[], redirectTo = '/spaces'): Promise<void> => {
  try {
    const hasPassed = await validateGuards(guards);

    if (!hasPassed) {
      throw redirect({ to: redirectTo });
    }
  } catch (error) {
    // If it's already a redirect, re-throw it
    if (error && typeof error === 'object' && 'to' in error) {
      throw error;
    }

    // Log unexpected errors to DataDog
    handleGuardError(error, 'Unexpected error during guard validation and redirect', { guards, redirectTo });

    // Fallback to redirect on error
    throw redirect({ to: redirectTo });
  }
};
